// Possibilités réelles du navigateur pour écouter un piano (micro, MIDI), indépendantes de
// React et testables sans navigateur : le parcours « Configurer mon piano » s'en sert pour
// ne proposer que ce qui fonctionne vraiment sur l'appareil et expliquer le reste.

export type OsFamily = 'ios' | 'android' | 'macos' | 'windows' | 'linux' | 'other';
export type BrowserFamily = 'safari' | 'chrome' | 'edge' | 'firefox' | 'other';

export interface Environment {
  userAgent: string;
  /** Contexte sécurisé (HTTPS ou localhost) : sans lui, ni micro ni MIDI. */
  secureContext: boolean;
  /** getUserMedia et AudioWorklet présents. */
  micApi: boolean;
  /** navigator.requestMIDIAccess présent. */
  midiApi: boolean;
  /** Points de contact tactiles : distingue un iPad, qui se présente comme un Mac. */
  maxTouchPoints: number;
}

export interface Availability {
  available: boolean;
  /** Explication courte quand ce n'est pas disponible. */
  reason: string | null;
}

export interface Capabilities {
  os: OsFamily;
  browser: BrowserFamily;
  secureContext: boolean;
  mic: Availability;
  midi: Availability;
  /** Libellé de l'appareil pour les messages (« iPhone ou iPad », « ordinateur »…). */
  deviceLabel: string;
  browserLabel: string;
}

export function detectEnvironment(): Environment {
  const nav = typeof navigator !== 'undefined' ? navigator : undefined;
  const hasAudioWorklet = typeof AudioContext !== 'undefined' && 'audioWorklet' in AudioContext.prototype;
  return {
    userAgent: nav?.userAgent ?? '',
    secureContext: typeof window !== 'undefined' ? window.isSecureContext !== false : true,
    micApi: !!nav?.mediaDevices && typeof nav.mediaDevices.getUserMedia === 'function' && hasAudioWorklet,
    midiApi: !!nav && typeof nav.requestMIDIAccess === 'function',
    maxTouchPoints: nav?.maxTouchPoints ?? 0,
  };
}

export function osOf(env: Environment): OsFamily {
  const ua = env.userAgent;
  if (/iPhone|iPad|iPod/.test(ua)) return 'ios';
  // iPadOS se présente comme un Mac de bureau, mais avec un écran tactile.
  if (/Macintosh/.test(ua) && env.maxTouchPoints > 1) return 'ios';
  if (/Android/.test(ua)) return 'android';
  if (/Macintosh|Mac OS X/.test(ua)) return 'macos';
  if (/Windows/.test(ua)) return 'windows';
  if (/Linux|CrOS/.test(ua)) return 'linux';
  return 'other';
}

export function browserOf(env: Environment): BrowserFamily {
  const ua = env.userAgent;
  if (/Edg\//.test(ua) || /EdgiOS\//.test(ua)) return 'edge';
  if (/Firefox\//.test(ua) || /FxiOS\//.test(ua)) return 'firefox';
  if (/CriOS\//.test(ua) || /Chrome\//.test(ua) || /Chromium\//.test(ua)) return 'chrome';
  if (/Safari\//.test(ua)) return 'safari';
  return 'other';
}

const DEVICE_LABELS: Record<OsFamily, string> = {
  ios: 'iPhone ou iPad',
  android: 'appareil Android',
  macos: 'Mac',
  windows: 'ordinateur Windows',
  linux: 'ordinateur',
  other: 'appareil',
};

const BROWSER_LABELS: Record<BrowserFamily, string> = {
  safari: 'Safari',
  chrome: 'Chrome',
  edge: 'Edge',
  firefox: 'Firefox',
  other: 'navigateur',
};

export const HTTPS_REASON = 'La page n’est pas ouverte en HTTPS : le navigateur cache alors le micro et le MIDI. Ouvrez l’adresse qui commence par https://.';

export function capabilitiesFor(env: Environment): Capabilities {
  const os = osOf(env);
  const browser = browserOf(env);
  let mic: Availability;
  if (!env.secureContext) mic = { available: false, reason: HTTPS_REASON };
  else if (!env.micApi) mic = { available: false, reason: 'Ce navigateur ne donne pas accès au micro ou à l’analyse audio en temps réel. Essayez une version récente de Safari, Chrome, Edge ou Firefox.' };
  else mic = { available: true, reason: null };

  let midi: Availability;
  if (env.midiApi && env.secureContext) midi = { available: true, reason: null };
  else if (os === 'ios') midi = { available: false, reason: 'Sur iPhone et iPad, aucun navigateur ne propose le MIDI (limite d’Apple). Utilisez le micro, ou un ordinateur avec Chrome, Edge ou Firefox.' };
  else if (!env.secureContext) midi = { available: false, reason: HTTPS_REASON };
  else if (browser === 'safari') midi = { available: false, reason: 'Safari ne propose pas le MIDI. Sur Mac, utilisez Chrome, Edge ou Firefox pour le câble MIDI, ou le micro.' };
  else midi = { available: false, reason: 'Ce navigateur ne propose pas le MIDI (Web MIDI). Utilisez Chrome, Edge ou Firefox, ou le micro.' };

  return { os, browser, secureContext: env.secureContext, mic, midi, deviceLabel: DEVICE_LABELS[os], browserLabel: BROWSER_LABELS[browser] };
}
